import {
    HttpTransportType,
    type HubConnection,
    HubConnectionBuilder,
    LogLevel,
} from '@microsoft/signalr';
import { ENGINE_API_URL } from '../constants/platform';

export interface McrDeployResult {
    success: boolean;
    message: string;
    messageId: string;
}

export interface ConnectionSetup {
    connectionReady: Promise<void>;
    result: Promise<McrDeployResult>;
}

let connection: HubConnection | null = null;
let activeTimeout: NodeJS.Timeout | null = null;

const connectToEngineSocket = (): ConnectionSetup => {
    let connectionReadyResolve: (() => void) | null = null;
    let connectionReadyReject: ((error: Error) => void) | null = null;

    const connectionReady = new Promise<void>((resolve, reject) => {
        connectionReadyResolve = resolve;
        connectionReadyReject = reject;
    });

    const result: Promise<McrDeployResult> = new Promise((resolve, reject) => {
        (async () => {
            // Clean up any existing connection FIRST
            if (connection !== null) {
                await cleanupConnection();
            }

            connection = new HubConnectionBuilder()
                .withUrl(`${ENGINE_API_URL ?? ''}/designtime`, {
                    skipNegotiation: true,
                    transport: HttpTransportType.WebSockets,
                })
                .configureLogging(LogLevel.Information)
                .build();

            const onDeployResult = (result: McrDeployResult) => {
                if (result.success) {
                    resolve(result);
                } else {
                    reject(new Error(result.message));
                }
            };

            const onReleaseDeployResult = (result: McrDeployResult) => {
                if (result.success) {
                    resolve(result);
                } else {
                    reject(new Error(result.message));
                }
            };

            connection.on('DeployResult', onDeployResult);
            connection.on('ReleaseDeployResult', onReleaseDeployResult);

            try {
                await connection.start();
                // Connection is ready!
                connectionReadyResolve?.();
            } catch {
                const errorMessage = 'Unable to start Cloud Socket Connection.';
                connectionReadyReject?.(new Error(errorMessage));
                connection = null;
                reject(new Error(errorMessage));
                return;
            }

            activeTimeout = setTimeout(async () => {
                reject(
                    new Error(
                        'Please ensure your MCR node is up to date and online as the publish attempt was not acknowledged.',
                    ),
                );
                await disconnectFromEngineSocket();
            }, 60000);
        })().catch(reject);
    });

    return { connectionReady, result };
};

const disconnectFromEngineSocket = async () => {
    await cleanupConnection();
};

const cleanupConnection = async () => {
    if (connection !== null) {
        if (activeTimeout !== null) {
            clearTimeout(activeTimeout);
            activeTimeout = null;
        }
        try {
            connection.off('DeployResult');
            connection.off('ReleaseDeployResult');
            await connection.stop();
        } catch (error) {
            // Ignore errors during cleanup - connection may already be stopped
            console.warn('Error during connection cleanup:', error);
        }
        connection = null;
    }
};

export { connectToEngineSocket, disconnectFromEngineSocket };
